import React, { useMemo, useState } from 'react';
import Alert from '@uber/base-icons/alert';
import { useSnackbar } from 'baseui/snackbar';
import { useAtomValue } from 'jotai';
import { partitionStaticAndDynamicQuestionnaire } from '../../../../../common/side-popover-panel/utils';
import type { AnnotationLabelT, AnswerT } from '../../../../../common/types/types';
import { questionnaireDetailsMapAtom } from '../../../../../modernization-common/side-panel/atoms/questionnaire.atom';
import { LONG_TOAST_DURATION } from '../../../../../modernization-common/side-panel/const';
import type { SelectedAnnotationDetailsT } from '../types';
interface UseEditAnnotationFormParams {
  selectedAnnotationDetails: SelectedAnnotationDetailsT | undefined;
  annotationLabels: AnnotationLabelT[];
}
export const useEditAnnotationForm = ({
  selectedAnnotationDetails,
  annotationLabels
}: UseEditAnnotationFormParams) => {
  const stateResetKey = `${selectedAnnotationDetails?.id}|${selectedAnnotationDetails?.annotationLabel.title}`;
  const [selectedLabel, setSelectedLabel] = useState<string | undefined>(() => selectedAnnotationDetails?.annotationLabel.title);
  const [questionnaireAnswers, setQuestionnaireAnswers] = useState<AnswerT | undefined>(() => selectedAnnotationDetails?.metadata?.answers);
  const [isAnswerValid, setIsAnswerValid] = useState(true);
  const [prevResetKey, setPrevResetKey] = useState(stateResetKey);
  if (prevResetKey !== stateResetKey) {
    setPrevResetKey(stateResetKey);
    setSelectedLabel(selectedAnnotationDetails?.annotationLabel.title);
    setQuestionnaireAnswers(selectedAnnotationDetails?.metadata?.answers);
  }
  const questionnaireDetailsMap = useAtomValue(questionnaireDetailsMapAtom);
  const {
    enqueue
  } = useSnackbar();
  const hasQuestionnaire = useMemo<boolean>(() => {
    const selectedQuestionnaireIds = annotationLabels.find(label => label.title === selectedLabel)?.questionnaire;
    if (!selectedQuestionnaireIds) return false;
    return selectedQuestionnaireIds?.some(questionnaire => {
      const questionnaireDetails = questionnaireDetailsMap[questionnaire.questionnaireId];
      if (questionnaireDetails?.status !== 'success') return false;
      const {
        staticQuestionnaire
      } = partitionStaticAndDynamicQuestionnaire(questionnaireDetails.questionnaire);
      return staticQuestionnaire.topQuestionIds.length > 0;
    });
  }, [annotationLabels, questionnaireDetailsMap, selectedLabel]);
  const compatibleLabels = useMemo(() => {
    const selectedLabelTool = annotationLabels.find(label => label.title === selectedLabel)?.tools?.[0];
    const currentAnnotationType = selectedAnnotationDetails?.annotationType;
    return annotationLabels.filter(label => {
      const labelTool = label.tools?.[0];

      // Check compatibility: if selected label has a tool, show labels with same tool or no tool
      // If selected label has no tool, show labels with same annotation type or no tool
      const isCompatible = selectedLabelTool ? !labelTool || labelTool === selectedLabelTool : !labelTool || labelTool === currentAnnotationType;
      return isCompatible;
    });
  }, [annotationLabels, selectedLabel, selectedAnnotationDetails?.annotationType]);
  const showToolMismatchError = () => {
    enqueue({
      message: 'Cannot change label due to incompatible annotation tool.',
      startEnhancer: () => React.createElement(Alert, {
        title: 'Error'
      })
    }, LONG_TOAST_DURATION);
  };
  const getSelectedLabelQuestionnaireId = () => {
    const selectedLabelData = annotationLabels.find(label => label.title === selectedLabel);
    const questionnaire = selectedLabelData?.questionnaire?.[0];
    return typeof questionnaire === 'string' ? questionnaire : questionnaire?.questionnaireId || '';
  };
  const handleLabelChange = (value: string) => {
    const currentLabelTool = annotationLabels.find(option => option.title === selectedLabel)?.tools?.[0];
    const newLabelTool = annotationLabels.find(option => option.title === value)?.tools?.[0];

    // Both current and new labels have tools - check if they're compatible
    if (currentLabelTool && newLabelTool) {
      if (currentLabelTool !== newLabelTool) {
        showToolMismatchError();
        return;
      }
    }
    // Current label has no tool but new label does - check against annotation type
    else if (!currentLabelTool && newLabelTool) {
      const currentAnnotationType = selectedAnnotationDetails?.annotationType;
      if (newLabelTool !== currentAnnotationType) {
        showToolMismatchError();
        return;
      }
    }
    // New label has no tool - allow switch without restrictions
    setQuestionnaireAnswers(undefined);
    setSelectedLabel(value);
  };
  const handleQuestionnaireChange = (isAnswerValid: boolean, answers?: AnswerT) => {
    setQuestionnaireAnswers(answers);
    setIsAnswerValid(isAnswerValid);
  };
  return {
    selectedLabel,
    questionnaireAnswers,
    isAnswerValid,
    hasQuestionnaire,
    compatibleLabels,
    handleLabelChange,
    handleQuestionnaireChange,
    getSelectedLabelQuestionnaireId
  };
};